<template>
  <div v-if="isDebugMode" class="card border-warning mb-3">
    <button
      class="card-header bg-warning bg-opacity-25 d-flex justify-content-between align-items-center py-1 px-3 cursor-pointer w-100 border-0 text-start"
      style="font: inherit; color: inherit"
      @click="open = !open"
      :aria-expanded="open"
    >
      <span class="fw-semibold small">
        <i class="bi bi-bug me-1"></i>Debug: Head Calculator
      </span>
      <i :class="open ? 'bi bi-chevron-up' : 'bi bi-chevron-down'"></i>
    </button>

    <div v-show="open" class="card-body py-2 px-3 small">
      <!-- Auto-compute from last readings -->
      <div v-if="readings" class="mb-2">
        <strong>From latest gauge readings:</strong>
        <table class="table table-sm table-borderless mb-1 mt-1">
          <tbody>
            <tr>
              <th scope="row" class="text-muted fw-normal" style="width: 160px">
                P<sub>s</sub> (I-1, atm)
              </th>
              <td>
                {{ readings.suctionPressure_psi.toFixed(3) }} psi &asymp; 0
              </td>
            </tr>
            <tr>
              <th scope="row" class="text-muted fw-normal">
                P<sub>d</sub> (I-2)
              </th>
              <td>{{ readings.dischargePressure_psi.toFixed(3) }} psi</td>
            </tr>
            <tr class="table-active">
              <th scope="row" class="fw-semibold">
                H = P<sub>d</sub> &times; 144 / &gamma;
              </th>
              <td class="fw-bold">{{ computedHead.toFixed(4) }} ft</td>
            </tr>
            <tr>
              <th scope="row" class="text-muted fw-normal">Q</th>
              <td>{{ readings.flowRate_GPM.toFixed(2) }} GPM</td>
            </tr>
          </tbody>
        </table>

        <div class="text-muted">
          True head (hidden): {{ readings._trueHead_ft.toFixed(4) }} ft
          &ensp;|&ensp; Error:
          {{
            (
              ((computedHead - readings._trueHead_ft) * 100) /
              readings._trueHead_ft
            ).toFixed(2)
          }}%
        </div>
      </div>

      <div v-else class="text-muted">Move the valve to generate readings.</div>

      <hr class="my-2" />

      <!-- Manual calculator -->
      <strong>Manual calculator:</strong>
      <div class="row g-2 mt-1">
        <div class="col">
          <label for="debug-manual-pd" class="form-label mb-0"
            >P<sub>d</sub> (psi)</label
          >
          <input
            id="debug-manual-pd"
            type="number"
            class="form-control form-control-sm"
            v-model.number="manualPd"
            step="0.01"
          />
        </div>
        <div class="col d-flex align-items-end pb-1">
          <span class="text-muted small">P<sub>s</sub> = 0 psi (atm)</span>
        </div>
      </div>
      <div v-if="manualHead !== null" class="mt-1 fw-semibold">
        H = {{ manualHead.toFixed(4) }} ft
      </div>

      <template v-if="fitResult">
        <hr class="my-2" />
        <strong>Computed curve fit:</strong>
        <table class="table table-sm table-borderless mb-0 mt-1">
          <tbody>
            <tr>
              <th scope="row" class="text-muted fw-normal" style="width: 40px">
                a
              </th>
              <td>{{ fitResult.a.toFixed(4) }} ft</td>
            </tr>
            <tr>
              <th scope="row" class="text-muted fw-normal">b</th>
              <td>{{ fitResult.b.toFixed(6) }} ft/GPM&sup2;</td>
            </tr>
            <tr class="table-active">
              <th scope="row" class="fw-semibold">R&sup2;</th>
              <td class="fw-bold">{{ fitResult.R2.toFixed(4) }}</td>
            </tr>
            <tr>
              <th scope="row" class="text-muted fw-normal">n</th>
              <td>{{ fitResult.n }} points</td>
            </tr>
          </tbody>
        </table>
        <div class="text-muted fst-italic">
          H<sub>p</sub>(Q) = {{ fitResult.a.toFixed(2) }} &minus;
          {{ fitResult.b.toFixed(6) }}&middot;Q&sup2;
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";

const GAMMA_LBFFT3 = 62.3; // specific weight γ of water at ~68 °F (lbf/ft³)
const IN2_PER_FT2 = 144;

export default {
  name: "DebugHeadCalculator",
  props: {
    readings: {
      type: Object,
      default: null
    },
    fitResult: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      open: true,
      manualPd: null
    };
  },
  computed: {
    ...mapGetters(["isDebugMode"]),
    deltaP_psi() {
      if (!this.readings) return 0;
      // P_s = 0 (atm), so ΔP = P_d
      return this.readings.dischargePressure_psi;
    },
    computedHead() {
      // H (ft) = P_d (psi) × 144 in²/ft² / γ (lbf/ft³)
      return (this.deltaP_psi * IN2_PER_FT2) / GAMMA_LBFFT3;
    },
    manualHead() {
      if (this.manualPd == null) return null;
      // P_s = 0, so H (ft) = P_d (psi) × 144 / γ
      return (this.manualPd * IN2_PER_FT2) / GAMMA_LBFFT3;
    }
  }
};
</script>
